import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { YogurtBand } from '../entities/yogurtBand.js';
import { HUD } from '../ui/hud.js';
import { addCrumbs } from '../core/save.js';
import { Groove } from '../audio/groove.js';
import { RhythmGame } from '../minigames/rhythm.js';
import { buildSong } from '../minigames/cafeSong.js';

// The Yogurt Café. You walk in and can roam the floor — the Dancing Fruit
// Yogurt are up on stage jamming — then step up to the stage to "help" them
// play (the rhythm minigame). States: 'explore' → 'playing' → 'results'.

export function createCafeScene(){
  return () => new CafeScene();
}

const STAGE_Z = -4.5;
const STAGE_H = 0.6;
const STAGE_FRONT = new THREE.Vector3(0, 0, -2.2);   // where you stand to help

class CafeScene {
  constructor(){
    this.state = 'explore';
    this.song = buildSong();
    this.groove = null;
    this.rhythm = null;
    this.panels = [];
    this.idleClock = 0;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x241C33);   // dim gig lighting

    this.camera = new THREE.PerspectiveCamera(52, window.innerWidth/window.innerHeight, 0.1, 100);
    // fixed audience view used while playing the set
    this.camBase = new THREE.Vector3(0, 3.4, 8.6);
    this.playTarget = new THREE.Vector3(0, 2.0, -3);
    // free-roam follow camera used while exploring
    this.camDist = 11;
    this.camTarget = new THREE.Vector3(0, 1.8, 1);
    this.azimuth = 0; this.polar = 0.5;

    this._buildRoom();
    this._buildStage();
    this._buildLights();

    this.band = new YogurtBand();
    this.band.root.position.set(0, STAGE_H, STAGE_Z);
    this.scene.add(this.band.root);

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 4);
    this.bread.root.rotation.y = Math.PI;               // facing the stage
    this.scene.add(this.bread.root);

    this.hotspots = [
      { pos: STAGE_FRONT.clone(), r: 2.6, label: '🎵 help the band play', action: () => this._start() },
      { pos: new THREE.Vector3(-7.6, 0, 3.5), r: 2.2, label: '← back to town', action: () => this.manager.goTo('town', { from: 'cafe' }) }
    ];
    this.nearSpot = null;
  }

  _buildRoom(){
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(20, 20),
      new THREE.MeshLambertMaterial({ color: C.rye })
    );
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true;
    this.scene.add(floor);

    const backWall = new THREE.Mesh(new THREE.PlaneGeometry(20, 9), new THREE.MeshLambertMaterial({ color: C.cobaltDim }));
    backWall.position.set(0, 4.5, -7); backWall.receiveShadow = true;
    this.scene.add(backWall);
    const sideL = backWall.clone(); sideL.rotation.y = Math.PI/2; sideL.position.set(-10, 4.5, 0); this.scene.add(sideL);
    const sideR = backWall.clone(); sideR.rotation.y = -Math.PI/2; sideR.position.set(10, 4.5, 0); this.scene.add(sideR);

    // café tables with stools, flanking the dance floor
    const table = (x, z) => {
      cyl(0.06, 0.06, 1.0, C.crustDark, x, 0.5, z, this.scene, 8);   // pole
      const top = cyl(0.6, 0.6, 0.08, C.crust, x, 1.0, z, this.scene, 16);
      top.castShadow = true;
      [[0.9,0],[-0.9,0],[0,0.9],[0,-0.9]].forEach(([dx,dz]) =>
        cyl(0.22, 0.22, 0.5, C.raspberry, x+dx, 0.25, z+dz, this.scene, 12));
    };
    table(-4.5, 2); table(4.5, 2); table(-5.5, -1.5); table(5.5, -1.5);

    // marquee over the stage
    const sign = textSprite('Yogurt Café', 34, '#FFF4DF', 5, 1.2);
    sign.position.set(0, 6.4, -6.7);
    this.scene.add(sign);

    // exit door (back-left)
    this.exitDoor = box(1.4, 2.4, 0.3, C.crustDark, -8.5, 1.2, 3.5, this.scene);
    const exitSign = textSprite('← exit', 26, '#FFF4DF', 2.0, 0.7);
    exitSign.position.set(-8.5, 2.9, 3.6);
    this.scene.add(exitSign);
  }

  _buildStage(){
    const stage = box(9, STAGE_H, 3.4, C.crustDark, 0, STAGE_H/2, STAGE_Z, this.scene);
    stage.receiveShadow = true;
    box(9, 0.1, 3.4, C.cocoa, 0, STAGE_H, STAGE_Z, this.scene);         // stage lip
    // amp stacks
    box(1, 1.2, 0.8, 0x1A1A22, -3.6, 0.6 + STAGE_H, STAGE_Z - 0.6, this.scene);
    box(1, 1.2, 0.8, 0x1A1A22,  3.6, 0.6 + STAGE_H, STAGE_Z - 0.6, this.scene);
    // backdrop banner behind the band
    box(6, 2.6, 0.15, C.raspberry, 0, 2.4, STAGE_Z - 1.4, this.scene);
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0x8888AA, 0.5));
    const key = new THREE.DirectionalLight(0xFFF2D8, 0.5);
    key.position.set(3, 9, 8); key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    this.scene.add(key);

    // colored stage lights that pulse to the beat
    const cols = [0xE6537B, 0x3D5FC0, 0x7FBF6A];
    this.stageLights = cols.map((col, i) => {
      const l = new THREE.PointLight(col, 0.0, 16, 2);
      l.position.set((i - 1) * 3, 4.4, STAGE_Z + 1.5);
      this.scene.add(l);
      return l;
    });
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.3, 0.72];
    this._toExplore(true);
  }

  exit(){
    this._teardownGame();
    this._clearPanels();
    this.input.onTap = null;
    HUD.showEnter(null); HUD.say('');
  }

  _toExplore(first){
    this.state = 'explore';
    this._clearPanels();
    this.bread.resetLimbs(); this.bread.drawFace('happy'); this._face = 'happy';
    this.input.onTap = (ndc, ray) => this._exploreTap(ray);
    HUD.setSubtitle('yogurt café');
    if (first) HUD.say('The Yogurt Café! The Dancing Fruit Yogurt are on stage. Click to walk — step up to the stage to help them play.');
    HUD.setHint('click to walk · walk up to the stage to jam, or to the exit');
    HUD.hideHintSoon(7000);
  }

  _exploreTap(ray){
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-9, Math.min(9, gp.x));
    gp.z = Math.max(-2.3, Math.min(8.5, gp.z));      // stay off the stage
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  // ---- panels (shared .cafe-* game UI) ----
  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  async _start(){
    this._clearPanels();
    this.state = 'playing';
    this.input.onTap = null;
    HUD.showEnter(null); HUD.setHint(''); HUD.hideHint(); HUD.say('');

    // stand in front of the stage, facing the band, for the audience view
    this.bread.stop();
    this.bread.root.position.set(0, 0, 3);
    this.bread.root.rotation.y = Math.PI; this.bread.facing = Math.PI;

    this.groove = new Groove(this.song);
    await this.groove.start();                 // user gesture → audio may play

    this.rhythm = new RhythmGame(this.song, this.groove, {
      onFinish: (r) => this._results(r)
    });
    this.rhythm.mount();
    this.rhythm.begin();

    // bail out of the set back to the floor
    const q = this._panel('<button class="cafe-quit">✕ leave</button>', 'bare');
    q.querySelector('.cafe-quit').onclick = () => { this._teardownGame(); this._toExplore(false); };
  }

  _results(r){
    this.state = 'results';
    this._teardownGame();
    if (r.crumbs > 0) addCrumbs(r.crumbs);
    HUD.refreshCrumbs();

    const grade = r.accuracy >= 95 ? 'S' : r.accuracy >= 85 ? 'A' : r.accuracy >= 70 ? 'B' : r.accuracy >= 50 ? 'C' : 'D';
    const p = this._panel(`
      <div class="cafe-card">
        <div class="cafe-grade">${grade}</div>
        <h2>Nice set!</h2>
        <div class="cafe-stats">
          <div><b>${r.perfect}</b><span>perfect</span></div>
          <div><b>${r.good}</b><span>good</span></div>
          <div><b>${r.miss}</b><span>miss</span></div>
          <div><b>${r.maxCombo}</b><span>max combo</span></div>
        </div>
        <p class="cafe-accuracy">${r.accuracy}% accuracy</p>
        <p class="cafe-payout">🍞 +${r.crumbs} crumbs</p>
        <button class="cafe-btn again">↻ play again</button>
        <button class="cafe-btn ghost leave">← back to the café</button>
      </div>`);
    p.querySelector('.again').onclick = () => { this._resetGame(); this._start(); };
    p.querySelector('.leave').onclick = () => this._toExplore(false);
  }

  _resetGame(){
    // fresh song instance so counts/notes reset
    this.song = buildSong();
  }

  _teardownGame(){
    if (this.rhythm){ this.rhythm.unmount(); this.rhythm = null; }
    if (this.groove){ this.groove.stop(); this.groove = null; }
    this.stageLights.forEach(l => l.intensity = 0);
  }

  update(dt){
    const playing = this.state === 'playing' && this.groove && this.groove.running;

    if (playing){
      // NB: rhythm.update() can end the set on the final note, tearing down the
      // groove mid-frame — re-check before touching it again.
      this.rhythm.update();
      if (this.state === 'playing' && this.groove){
        const phase = this.groove.beatPhase();
        const energy = Math.min(1, (this.rhythm.combo || 0) / 20);
        this.band.update(dt, phase, 0.35 + energy * 0.65);
        this._danceBread(phase, energy);
        const pulse = Math.max(0, Math.sin(phase * Math.PI * 2));
        this.stageLights.forEach((l, i) => {
          l.intensity = (0.4 + pulse * 1.6) * (0.6 + 0.4 * Math.sin(phase + i));
        });
      }
      // locked audience view with a subtle live sway
      const sway = Math.sin((performance.now() % 100000) * 0.0004);
      this.camera.position.set(this.camBase.x + sway * 0.4, this.camBase.y, this.camBase.z);
      this.camera.lookAt(this.playTarget);
      return;
    }

    // explore / results: band still grooves gently, Bread Boi roams
    this.idleClock += dt;
    this.band.update(dt, this.idleClock * 0.8, 0.25);
    this.stageLights.forEach((l, i) => l.intensity = 0.3 + 0.2 * Math.sin(this.idleClock * 1.5 + i));
    this.bread.update(dt);

    if (this.state === 'explore'){
      let near = null, nd = Infinity;
      this.hotspots.forEach(h => {
        const d = h.pos.distanceTo(this.bread.root.position);
        if (d < nd){ nd = d; near = h; }
      });
      if (near && nd < near.r){
        if (this.nearSpot !== near){ this.nearSpot = near; HUD.showEnter(near.label, () => near.action()); }
      } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }
    }

    // free-roam follow camera
    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.4, 1.8, this.bread.root.position.z * 0.4), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  _danceBread(phase, energy){
    const hop = Math.max(0, Math.sin(phase * Math.PI * 2));
    this.bread.root.position.y = hop * (0.06 + energy * 0.12);
    this.bread.body.rotation.z = Math.sin(phase * Math.PI) * 0.12;
    this.bread.armL.shoulder.rotation.x = -0.4 - hop * 0.9 * energy;
    this.bread.armR.shoulder.rotation.x = -0.4 - Math.max(0, Math.sin(phase*Math.PI*2 + Math.PI)) * 0.9 * energy;
    const face = this.rhythm && this.rhythm.combo >= 8 ? 'yum' : this.rhythm && this.rhythm.combo >= 3 ? 'grin' : 'happy';
    if (face !== this._face){ this.bread.drawFace(face); this._face = face; }
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    if (this.rhythm) this.rhythm._measure();
  }
}
